import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Banner, Card } from '../components/ui';
import { authApi } from '../api/client';

type VerifyState = 'verifying' | 'success' | 'failed';

/**
 * PUBLIC /verify-email?token=<raw> — confirm an email address on mount.
 *
 * SOFT verification: the user may already be logged in, so on success we offer
 * a link to /apps as well as /login.
 */
export function VerifyEmailPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [state, setState] = useState<VerifyState>('verifying');
  // Guard against double-invocation under React 18 StrictMode in dev.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!token) {
      setState('failed');
      return;
    }

    let active = true;
    authApi
      .verifyEmail(token)
      .then(() => {
        if (active) setState('success');
      })
      .catch(() => {
        if (active) setState('failed');
      });

    return () => {
      active = false;
    };
  }, [token]);

  return (
    <div className="auth-screen">
      <div className="auth-box">
        <h1 className="auth-title">App Builder</h1>
        <Card title="Verify your email">
          {state === 'verifying' && <Banner tone="info">Verifying your email…</Banner>}

          {state === 'success' && (
            <>
              <Banner tone="success">Your email has been verified.</Banner>
              <p className="auth-alt">
                <Link to="/apps">Go to your apps</Link> · <Link to="/login">Sign in</Link>
              </p>
            </>
          )}

          {state === 'failed' && (
            <>
              <Banner>This verification link is invalid or has expired.</Banner>
              <p className="auth-alt">
                <Link to="/login">Back to sign in</Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
